/*
 * AdminUserCommentsTab.jsx
 *
 * Admin panel view to moderate real user testimonials (approve / reject /
 * delete). Consumed by AdminDashboard as the "Testimonios" tab.
 */

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Check, X, Trash2, RefreshCw, MessageSquareQuote,
  Clock, ShieldCheck, ShieldX, Star, Pencil,
} from "lucide-react";
import { AdminCommentEditor } from "@/components/admin/AdminCommentEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: { label: "Pendientes", color: "bg-amber-500/15 border-amber-500/40 text-amber-300" },
  approved: { label: "Aprobados", color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  rejected: { label: "Rechazados", color: "bg-red-500/15 border-red-500/40 text-red-300" },
};

export const AdminUserCommentsTab = ({ authHeader }) => {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [actionBusy, setActionBusy] = useState({}); // {id: bool}
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/user-comments?status=${filter}&limit=300`,
        { headers: authHeader?.() || {} });
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setPendingCount(r.data?.pending_count || 0);
    } catch (e) {
      toast.error("No se pudieron cargar los testimonios");
    } finally {
      setLoading(false);
    }
  }, [filter, authHeader]);

  useEffect(() => { load(); }, [load]);

  const doApprove = async (id) => {
    setActionBusy((s) => ({ ...s, [id]: true }));
    try {
      await axios.post(`${API}/admin/user-comments/${id}/approve`, {},
        { headers: authHeader?.() || {} });
      toast.success("Testimonio aprobado — visible ya en /testimonios");
      load();
    } catch (e) { toast.error("Error al aprobar"); }
    finally { setActionBusy((s) => ({ ...s, [id]: false })); }
  };

  const doReject = async (id) => {
    if (!window.confirm("¿Rechazar este testimonio? Ya no se publicará.")) return;
    setActionBusy((s) => ({ ...s, [id]: true }));
    try {
      await axios.post(`${API}/admin/user-comments/${id}/reject`, {},
        { headers: authHeader?.() || {} });
      toast.success("Testimonio rechazado");
      load();
    } catch (e) { toast.error("Error al rechazar"); }
    finally { setActionBusy((s) => ({ ...s, [id]: false })); }
  };

  const doDelete = async (id) => {
    if (!window.confirm("¿Eliminar definitivamente este testimonio?")) return;
    setActionBusy((s) => ({ ...s, [id]: true }));
    try {
      await axios.delete(`${API}/admin/user-comments/${id}`,
        { headers: authHeader?.() || {} });
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error("Error al eliminar"); }
    finally { setActionBusy((s) => ({ ...s, [id]: false })); }
  };

  return (
    <div className="space-y-4" data-testid="admin-user-comments-tab">
      <div className="glass-dark rounded-2xl p-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-[#d4af37] mr-3">
          <MessageSquareQuote className="w-4 h-4" />
          <div>
            <div className="text-white text-sm font-semibold">Testimonios de usuarios</div>
            <div className="text-[11px] text-neutral-500">
              Aprueba, rechaza o elimina. Los aprobados aparecen en /testimonios con 0 me gusta.
            </div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {(["pending", "approved", "rejected"]).map((k) => {
            const active = filter === k;
            const meta = STATUS_LABEL[k];
            return (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => setFilter(k)}
                className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-all ${
                  active ? meta.color : "border-white/10 text-neutral-400 hover:text-white"
                }`}
              >
                {meta.label}
                {k === "pending" && pendingCount > 0 && (
                  <span className="ml-1.5 bg-red-500 text-white rounded-full text-[10px] px-1.5">
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
          <button
            data-testid="user-comments-refresh"
            onClick={load}
            className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-neutral-300 hover:text-[#d4af37] hover:border-[#d4af37]/40 inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Recargar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="glass-dark rounded-2xl p-8 text-center text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...
        </div>
      ) : items.length === 0 ? (
        <div className="glass-dark rounded-2xl p-10 text-center text-neutral-500 italic">
          Sin testimonios en esta lista.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`user-comment-${c.id}`}
              className="glass-dark rounded-xl p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 border border-white/10 rounded-full px-2 py-0.5">
                      {c.country || "—"}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 border border-white/10 rounded-full px-2 py-0.5">
                      {c.service}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 border border-white/10 rounded-full px-2 py-0.5">
                      {c.lang}
                    </span>
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`w-3 h-3 ${s <= (c.rating || 5) ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`}
                        />
                      ))}
                    </span>
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                    {c.approved_at && <span className="text-emerald-400">· aprobado {new Date(c.approved_at).toLocaleDateString()}</span>}
                    {c.rejected_at && <span className="text-red-400">· rechazado {new Date(c.rejected_at).toLocaleDateString()}</span>}
                    {c.submitted_ip && filter === "pending" && (
                      <span className="ml-1 font-mono text-neutral-600">· IP {c.submitted_ip}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    data-testid={`user-comment-edit-${c.id}`}
                    onClick={() => setEditingId(editingId === c.id ? null : c.id)}
                    className="text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 flex items-center gap-1"
                    title="Editar nombre, país, calificación o comentario"
                  >
                    <Pencil className="w-3 h-3" /> Editar
                  </button>
                  {c.status !== "approved" && (
                    <button
                      data-testid={`user-comment-approve-${c.id}`}
                      disabled={actionBusy[c.id]}
                      onClick={() => doApprove(c.id)}
                      className="text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1 disabled:opacity-50"
                      title="Aprobar y publicar"
                    >
                      {actionBusy[c.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Aprobar
                    </button>
                  )}
                  {c.status !== "rejected" && (
                    <button
                      data-testid={`user-comment-reject-${c.id}`}
                      disabled={actionBusy[c.id]}
                      onClick={() => doReject(c.id)}
                      className="text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1 disabled:opacity-50"
                    >
                      {actionBusy[c.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldX className="w-3 h-3" />} Rechazar
                    </button>
                  )}
                  <button
                    data-testid={`user-comment-delete-${c.id}`}
                    disabled={actionBusy[c.id]}
                    onClick={() => doDelete(c.id)}
                    className="text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1 disabled:opacity-50"
                    title="Eliminar definitivamente"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-line">{c.text}</p>
              {editingId === c.id && (
                <AdminCommentEditor
                  comment={c}
                  authHeader={authHeader}
                  onCancel={() => setEditingId(null)}
                  onSaved={() => { setEditingId(null); load(); }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
